/*!
 * Baby In — PDP shipping note (main product form only)
 *
 * Keeps snippets/bi-shipping-note.liquid in sync with the selected variant,
 * the quantity input and the cart. The shipping RULES are not defined here:
 * they come from the single config block in the snippet (JSON config).
 *
 * Cart access is read-only (GET {locale root}cart.js). It never adds, changes
 * or clears cart items, never touches the form's submit handling, does not
 * poll, and does not patch fetch/XHR.
 *
 * computeShipping() is the production calculation; it is also exported for
 * Node (module.exports) so the tests in work/ run this exact code.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- core */

  function toInt(value) {
    var n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof n === 'number' && isFinite(n) && Math.floor(n) === n ? n : null;
  }

  function parseQuantity(value, fallback) {
    var n = parseInt(value, 10);
    if (!isFinite(n) || n < 1) n = parseInt(fallback, 10);
    if (!isFinite(n) || n < 1) n = 1;
    return n;
  }

  function isFixedId(id, rules) {
    return (rules.fixedIds || []).map(String).indexOf(String(id)) !== -1;
  }

  /**
   * Projected domestic shipping for the existing cart PLUS `quantity` of
   * `variant` (before it is added).
   *
   * input.rules   { currency, generalRate, freeMin, fixedRate, fixedIds }
   * input.cart    { total_price, currency, items: [{ variant_id, requires_shipping }] }
   *               (total_price = cart total after known discounts, subunits)
   * input.variant { id, price, available, requires_shipping } or null
   * input.quantity
   *
   * Returns { status: 'hidden' | 'fallback' | 'free' | 'rate', amount, projectedTotal, reason }
   */
  function computeShipping(input) {
    var rules = input && input.rules;
    var cart = input && input.cart;
    var variant = input && input.variant;

    if (!variant) return { status: 'hidden', amount: null, reason: 'no_variant' };
    if (variant.available !== true) return { status: 'hidden', amount: null, reason: 'unavailable' };
    if (variant.requires_shipping !== true) return { status: 'hidden', amount: null, reason: 'no_shipping' };
    if (!rules || !cart) return { status: 'fallback', amount: null, reason: 'no_data' };
    if (String(cart.currency || '').toUpperCase() !== String(rules.currency).toUpperCase()) {
      return { status: 'fallback', amount: null, reason: 'currency' };
    }

    var total = toInt(cart.total_price);
    var price = toInt(variant.price);
    if (total === null || price === null) return { status: 'fallback', amount: null, reason: 'bad_amount' };

    var quantity = parseQuantity(input.quantity, 1);
    var projectedTotal = total + price * quantity;

    var hasFixed = false;
    var hasGeneral = false;
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].requires_shipping !== true) continue;
      if (isFixedId(items[i].variant_id, rules)) hasFixed = true; else hasGeneral = true;
    }
    if (isFixedId(variant.id, rules)) hasFixed = true; else hasGeneral = true;

    var amount = 0;
    if (hasFixed) amount += rules.fixedRate;
    if (hasGeneral && projectedTotal < rules.freeMin) amount += rules.generalRate;

    return {
      status: amount === 0 ? 'free' : 'rate',
      amount: amount,
      projectedTotal: projectedTotal,
      reason: null
    };
  }

  function formatMajor(subunits) {
    return subunits % 100 === 0 ? String(subunits / 100) : (subunits / 100).toFixed(2);
  }

  function labelFor(result, strings) {
    if (!result || result.status === 'hidden') return '';
    if (result.status === 'fallback') return strings.checkout;
    if (result.status === 'free') return strings.free;
    return String(strings.rate).replace('__BI_AMOUNT__', formatMajor(result.amount));
  }

  var api = {
    computeShipping: computeShipping,
    labelFor: labelFor,
    formatMajor: formatMajor,
    parseQuantity: parseQuantity
  };

  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- DOM */

  if (window.BIShippingNote && window.BIShippingNote.booted) {
    window.BIShippingNote.scan(document);
    return;
  }

  var NOTE_SELECTOR = '[data-bi-shipping-note]';
  var CART_COUNT_SELECTOR = '[data-cart-count]';
  var CART_DRAWER_SELECTOR = '#t4s-mini_cart';
  var CART_EVENTS = ['cart:refresh', 'cart:updated', 'cart:change'];
  var instances = [];

  function cartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart.js';
  }

  /* ---- cart reads: debounced, coalesced, newest response wins ---- */

  var cartTimer = null;
  var cartSeq = 0;
  var cartController = null;

  function requestCart() {
    if (!instances.length) return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(fetchCart, 250);
  }

  function fetchCart() {
    var seq = ++cartSeq;
    if (cartController) { try { cartController.abort(); } catch (e) { /* no-op */ } }
    cartController = typeof AbortController === 'function' ? new AbortController() : null;
    window.fetch(cartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: cartController ? cartController.signal : undefined
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    }).then(function (data) {
      if (seq !== cartSeq) return; /* a newer request superseded this one */
      var cart = {
        total_price: data.total_price,
        currency: data.currency,
        items: (data.items || []).map(function (it) {
          return { variant_id: it.variant_id, requires_shipping: it.requires_shipping === true };
        })
      };
      for (var i = 0; i < instances.length; i++) instances[i].setCart(cart);
    }).catch(function () { /* keep the last known cart */ });
  }

  /* ---- narrow cart observers (count badge + drawer only) ---- */

  var cartObserver = null;

  function watchCart() {
    if (cartObserver || typeof MutationObserver !== 'function') return;
    cartObserver = new MutationObserver(requestCart);
    var nodes = document.querySelectorAll(CART_COUNT_SELECTOR + ',' + CART_DRAWER_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].closest(NOTE_SELECTOR)) continue;
      cartObserver.observe(nodes[i], { childList: true, characterData: true, subtree: true });
    }
  }

  /* ---- one note instance per main product form ---- */

  function Note(el) {
    this.el = el;
    var cfgNode = el.querySelector('script[data-bi-shipping-note-config]');
    this.cfg = JSON.parse(cfgNode.textContent);
    this.form = document.getElementById(this.cfg.formId);
    this.container = el.closest('[data-product-featured]');
    this.amountEl = el.querySelector('[data-bi-ship-amount]');
    this.timingEl = el.querySelector('[data-bi-ship-timing]');
    this.cart = this.cfg.cart;
    this.variantId = this.cfg.variantId;
    this.variants = {};
    for (var i = 0; i < this.cfg.variants.length; i++) {
      this.variants[String(this.cfg.variants[i].id)] = this.cfg.variants[i];
    }
    this.cleanup = [];
    this.bind();
    this.render();
  }

  Note.prototype.isMain = function () {
    if (!this.container) return false;
    try {
      return JSON.parse(this.container.getAttribute('data-product-featured')).isMainProduct === true;
    } catch (e) {
      return true; /* Liquid already limited rendering to type 'main' */
    }
  };

  Note.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.cleanup.push(function () { target.removeEventListener(type, fn, opts); });
  };

  Note.prototype.quantityInput = function () {
    return this.form ? this.form.querySelector('input[name="quantity"]') : null;
  };

  Note.prototype.bind = function () {
    var self = this;
    var render = function () { self.render(); };
    var renderSoon = function () { setTimeout(render, 0); };

    if (this.form) {
      this.on(this.form, 'change', function (e) {
        var t = e.target;
        if (t && t.name === 'id') self.variantId = t.value || null;
        render();
      });
      this.on(this.form, 'input', function (e) {
        if (e.target && e.target.name === 'quantity') render();
      });
      /* qty +/- buttons: the theme updates the value in its own click handler */
      this.on(this.form, 'click', function (e) {
        if (e.target && e.target.closest && e.target.closest('[data-quantity-selector]')) renderSoon();
      });
      /* programmatic qty changes (theme uses .attr('value').val()) */
      var qty = this.quantityInput();
      if (qty && typeof MutationObserver === 'function') {
        var mo = new MutationObserver(render);
        mo.observe(qty, { attributes: true, attributeFilter: ['value', 'min'] });
        this.cleanup.push(function () { mo.disconnect(); });
      }
    }
    this.bindTheme();
  };

  /* theme: jQuery_T4NT(container).trigger({type:'variant:changed', currentVariant}) */
  Note.prototype.bindTheme = function () {
    var $ = window.jQuery_T4NT;
    if (this.themeBound || !$ || !this.container) return;
    this.themeBound = true;
    var self = this;
    var $c = $(this.container);
    $c.off('variant:changed.biShipNote').on('variant:changed.biShipNote', function (e) {
      var v = e && e.currentVariant;
      if (v && v.id != null) {
        var known = self.variants[String(v.id)] || {};
        self.variants[String(v.id)] = {
          id: v.id,
          price: v.price != null ? v.price : known.price,
          available: typeof v.available === 'boolean' ? v.available : known.available,
          requires_shipping: typeof v.requires_shipping === 'boolean' ? v.requires_shipping : known.requires_shipping,
          std: known.std === true
        };
        self.variantId = v.id;
      } else {
        self.variantId = null;
      }
      self.render();
    });
    this.cleanup.push(function () { $c.off('variant:changed.biShipNote'); });
  };

  Note.prototype.setCart = function (cart) {
    this.cart = cart;
    this.render();
  };

  Note.prototype.render = function () {
    var variant = this.variantId != null ? this.variants[String(this.variantId)] || null : null;
    var qtyInput = this.quantityInput();
    var quantity = parseQuantity(qtyInput ? qtyInput.value : null, this.cfg.quantity);
    var result = computeShipping({ rules: this.cfg.rules, cart: this.cart, variant: variant, quantity: quantity });

    if (result.status === 'hidden') {
      if (!this.el.hidden) this.el.hidden = true;
      return;
    }
    var label = labelFor(result, this.cfg.strings);
    if (this.amountEl.textContent !== label) this.amountEl.textContent = label;
    var std = !!(variant && variant.std);
    if (this.timingEl && this.timingEl.hidden === std) this.timingEl.hidden = !std;
    if (this.el.hidden) this.el.hidden = false;
  };

  Note.prototype.destroy = function () {
    for (var i = 0; i < this.cleanup.length; i++) this.cleanup[i]();
    this.cleanup = [];
  };

  /* ---- registry, section reload support, global listeners (once) ---- */

  function scan(root) {
    var nodes = (root || document).querySelectorAll(NOTE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.__biShipNote) continue;
      try {
        var note = new Note(el);
        if (!note.isMain()) { note.destroy(); el.hidden = true; continue; }
        el.__biShipNote = note;
        instances.push(note);
      } catch (e) { /* leave the server-rendered state */ }
    }
    watchCart();
  }

  function teardown(root) {
    instances = instances.filter(function (note) {
      if (root && !root.contains(note.el) && document.contains(note.el)) return true;
      note.destroy();
      note.el.__biShipNote = null;
      return false;
    });
  }

  function boot() {
    scan(document);
    for (var i = 0; i < CART_EVENTS.length; i++) document.addEventListener(CART_EVENTS[i], requestCart);
    window.addEventListener('focus', requestCart);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') requestCart();
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) requestCart(); });
    /* jQuery_T4NT may initialise after this file */
    window.addEventListener('load', function () {
      for (var j = 0; j < instances.length; j++) instances[j].bindTheme();
    });
    document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
    document.addEventListener('shopify:section:load', function (e) {
      teardown(e.target);
      if (cartObserver) { cartObserver.disconnect(); cartObserver = null; }
      scan(e.target);
    });
  }

  window.BIShippingNote = { booted: true, scan: scan, compute: computeShipping };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
